import type { Branding } from "@/theme/branding";

/** How the controller fetches a tenant's branding (injected so it is testable). */
export type FetchBranding = (
  apiBase: string,
  slug: string,
  signal?: AbortSignal,
) => Promise<Branding | null>;

/**
 * Applies (or clears) the resolved branding. Called with the resolved `Branding`
 * on success, or `null` to reset to the neutral default (empty brand style).
 * The host element wires this to: set the brand <style> textContent, inject any
 * asset fonts, and re-render so the logo appears.
 */
export type ApplyCss = (branding: Branding | null) => void;

export interface BrandingController {
  /**
   * Fetch + apply branding for a (slug, apiBase) pair. Dedups by key: a repeat
   * `start` with the same key (already started) does NOT refetch. Empty slug or
   * apiBase stays neutral (no fetch, applies null). Safe across reconnect.
   */
  start(slug: string, apiBase: string): Promise<void>;
  /**
   * Reconnect/disconnect hook: aborts any in-flight fetch and — only when no
   * branding ever resolved — clears the cached key so a fresh fetch fires on the
   * next `start`. When branding already resolved, the key is retained (the host's
   * persistent style node keeps the CSS; no refetch, no flash).
   */
  reset(): void;
  /** The last resolved branding (null = neutral). Read by the host to render the logo. */
  readonly branding: Branding | null;
}

/**
 * Owns the branding lifecycle previously interleaved in the custom-element
 * god-object: brand-key dedup, abort, the reconnect-reset rule, and CSS
 * application. Plain object — testable without a custom-element harness.
 */
export function createBrandingController({
  fetchBranding,
  applyCss,
}: {
  fetchBranding: FetchBranding;
  applyCss: ApplyCss;
}): BrandingController {
  let brandKey = ""; // the slug|apiBase pair we last started a fetch for
  let branding: Branding | null = null;
  let abort: AbortController | null = null;

  const controller: BrandingController = {
    get branding() {
      return branding;
    },

    async start(slug: string, apiBase: string): Promise<void> {
      const key = `${slug}|${apiBase}`;
      if (key === brandKey) return;
      brandKey = key;
      branding = null;
      applyCss(null); // clear to neutral while (re)resolving
      abort?.abort();
      abort = null;
      if (!apiBase || !slug) return; // no fetch → neutral default

      const ctrl = new AbortController();
      abort = ctrl;
      const resolved = await fetchBranding(apiBase, slug, ctrl.signal);
      // Ignore a stale resolve: the key changed (or fetch was aborted) mid-flight.
      if (ctrl.signal.aborted || key !== brandKey) return;
      branding = resolved;
      applyCss(resolved);
    },

    reset(): void {
      abort?.abort();
      abort = null;
      // Only reset the key when branding never resolved (fetch was in-flight and
      // just aborted, or none started). This forces a fresh fetch on reconnect.
      // When branding already resolved, keep the key to short-circuit the next
      // start — no unnecessary round-trip and no style flash on reconnect.
      if (branding === null) brandKey = "";
    },
  };

  return controller;
}
